import React, { useState, useEffect, useCallback } from "react";
import "./D01_Demographics.css";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import Header from "../components/Header";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import D01BarChartViz01 from "../components/Dashboardviz/D01/DB01Viz1";
import D01BarChartViz02 from "../components/Dashboardviz/D01/DB01Viz2";
import D01BarChartViz03 from "../components/Dashboardviz/D01/DB01Viz3";
import D01BarChartViz04 from "../components/Dashboardviz/D01/DB01Viz4";
import D01BarChartViz05 from "../components/Dashboardviz/D01/DB01Viz5";
import D01BarChartViz06 from "../components/Dashboardviz/D01/DB01Viz6";
import D01BarChartViz07 from "../components/Dashboardviz/D01/DB01Viz7";
import D01BarChartViz08 from "../components/Dashboardviz/D01/DB01Viz8";
import D01BarChartViz09 from "../components/Dashboardviz/D01/DB01Viz9";
import D01BarChartViz10 from "../components/Dashboardviz/D01/Db01Viz10";
import RespondentsSummary from "../components/Dashboardviz/D01/DB01Viz11";
import { useFilter } from "../FilterContext";
import { FILTER_CONFIG } from "../config/filterconfig";
import jsonData from "../current-survey-numeric.json";

const D01_Demographics = () => {
  const { filterCriteria } = useFilter();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Toggle navigation sidebar
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  // Toggle filter sidebar
  const toggleFilterSidebar = useCallback(() => {
    setIsFilterSidebarOpen((prev) => !prev);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const generateFilterOptions = () => {
      const options = {};

      FILTER_CONFIG.forEach(({ fieldName, jsonKey, sortOrder }) => {
        let uniqueValues = [...new Set(jsonData.map((item) => item[jsonKey]))];
        uniqueValues = uniqueValues.filter(
          (value) => value !== null && value !== undefined
        );

        if (sortOrder) {
          // Identify values not present in sortOrder
          const missingValues = uniqueValues.filter(
            (val) => !sortOrder.includes(val)
          );

          if (missingValues.length > 0) {
            console.warn(`⚠️ Missing values in ${fieldName}:`, missingValues);
          }

          // Enforce custom sort order while handling missing values
          uniqueValues = uniqueValues.sort((a, b) => {
            const indexA = sortOrder.indexOf(a);
            const indexB = sortOrder.indexOf(b);

            if (indexA === -1 && indexB === -1) return a.localeCompare(b); // Alphabetical only if both are missing
            if (indexA === -1) return 1; // Push missing values to the end
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
        }

        options[fieldName] = uniqueValues;
      });

      return options;
    };

    setFilterOptions(generateFilterOptions());
  }, []);

  // Ensure charts resize properly when window size changes
  useEffect(() => {
    const resizeCharts = () => {
      // If you're using ApexCharts
      if (window.ApexCharts) {
        const charts = document.querySelectorAll('.apexcharts-canvas');
        charts.forEach(chart => {
          try {
            chart.style.width = '100%';
            const chartInstance = window.ApexCharts.getChartByID(
              chart.closest('.apexchart-container')?.id
            );
            if (chartInstance) {
              chartInstance.updateOptions({
                chart: {
                  width: '100%'
                }
              });
            }
          } catch (e) {
            console.error("Chart resize error:", e);
          }
        });
      }
    };
    
    // Use a small delay to ensure DOM is ready
    const timer = setTimeout(resizeCharts, 100);
    
    return () => clearTimeout(timer);
  }, [windowSize]);

  return (
    <div className="ccidemographics-dashboard-container">
      <Header
        toggleSidebar={toggleSidebar}
        toggleFilterSidebar={toggleFilterSidebar}
      />
      <NavigationSidebar
        isOpen={isSidebarOpen}
        className={
          isSidebarOpen
            ? "navigation-sidebar-open"
            : "navigation-sidebar-closed"
        }
      />
      <FilterSidebar
        isOpen={isFilterSidebarOpen}
        filterOptions={filterOptions}
      />
      <div className="ccidemographics-main-content">
        <div className="ccidemographics-grid-container">
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Location</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz01 filterCriteria={filterCriteria} />
            </div>
          </div>
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Department</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz02 filterCriteria={filterCriteria} />
            </div>
          </div>
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Functional Area</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz03 filterCriteria={filterCriteria} />
            </div>
          </div>
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Job Classification</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz04 filterCriteria={filterCriteria} />
            </div>
          </div>
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Tenure</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz05 filterCriteria={filterCriteria} />
            </div>
          </div>
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Generation</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz06 filterCriteria={filterCriteria} />
            </div>
          </div>
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Gender</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz07 filterCriteria={filterCriteria} />
            </div>
          </div>
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Manager Name</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz08 filterCriteria={filterCriteria} />
            </div>
          </div>
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Race</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz09 filterCriteria={filterCriteria} />
            </div>
          </div>
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Employee Type</h2>
            <div className="ccidemographics-card-content">
              <D01BarChartViz10 filterCriteria={filterCriteria} />
            </div>
          </div>
        </div>
        <div className="ccidemographics-right-sidebar">
          <div className="ccidemographics-card">
            <h2 className="ccidemographics-card-header">Respondents Summary</h2>
            <div className="ccidemographics-card-content">
              <RespondentsSummary filterCriteria={filterCriteria} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default D01_Demographics;